/**
 * Formats d'affichage, par `Intl`.
 *
 * Un prix rond s'écrit sans décimales : « 12 000 € », « €12,000 ». Les
 * dimensions s'écrivent comme sur un cartel : « 120 × 80 cm ». Rien n'est
 * codé en dur, tout passe par `Intl` et la langue de la personne.
 */
import { AppError } from '../errors.js';
/** La locale ICU de chaque langue du produit. */
export const LOCALES = {
    fr: 'fr-FR',
    en: 'en-GB',
};
/** La locale ICU d'une langue. */
export function locale(lang) {
    return LOCALES[lang];
}
/**
 * Un prix, à partir de centiemes entiers.
 *
 * « 12 000 € » en français, « €12,000 » en anglais. Les centimes n'apparaissent
 * que s'il y en a.
 */
export function formatPrice(cents, currency, lang) {
    if (!Number.isInteger(cents)) {
        throw new AppError('validation.invalide', 'Un prix s’exprime en centimes entiers.', { cents });
    }
    const rond = cents % 100 === 0;
    return new Intl.NumberFormat(locale(lang), {
        style: 'currency',
        currency,
        minimumFractionDigits: rond ? 0 : 2,
        maximumFractionDigits: rond ? 0 : 2,
    }).format(cents / 100);
}
/** Un nombre, avec au plus deux décimales et la virgule de la langue. */
export function formatNumber(valeur, lang, decimales = 2) {
    return new Intl.NumberFormat(locale(lang), {
        maximumFractionDigits: decimales,
    }).format(valeur);
}
/** Convertit les dimensions du modèle vers la forme courte de l'affichage. */
export function dimensionsAffichables(dimensions) {
    return dimensions.profondeur === undefined
        ? { h: dimensions.hauteur, w: dimensions.largeur }
        : { h: dimensions.hauteur, w: dimensions.largeur, d: dimensions.profondeur };
}
/**
 * Des dimensions, en centimètres : « 120 × 80 cm », « 120 × 80 × 5 cm ».
 *
 * Le séparateur est le signe multiplié (U+00D7), comme sur un cartel.
 */
export function formatDimensions({ h, w, d }, lang, unite = 'cm') {
    const parties = [h, w, ...(d === undefined ? [] : [d])].map((valeur) => formatNumber(valeur, lang));
    return `${parties.join(' × ')} ${unite}`;
}
const STYLES_DATE = {
    court: { dateStyle: 'short' },
    long: { dateStyle: 'long' },
    mois: { year: 'numeric', month: 'long' },
};
/** Une date : « 22 septembre 2026 », « 22 September 2026 ». */
export function formatDate(date, lang, longueur = 'long') {
    if (Number.isNaN(date.getTime())) {
        throw new AppError('validation.invalide', 'Date illisible.', { date: String(date) });
    }
    return new Intl.DateTimeFormat(locale(lang), {
        ...STYLES_DATE[longueur],
        timeZone: 'Europe/Paris',
    }).format(date);
}
/** Un intervalle de dates, pour une exposition ou une foire. */
export function formatDateRange(debut, fin, lang) {
    return new Intl.DateTimeFormat(locale(lang), {
        dateStyle: 'long',
        timeZone: 'Europe/Paris',
    }).formatRange(debut, fin);
}
/**
 * Un numéro d'inventaire, sous sa forme d'affichage et de comparaison :
 * majuscules, espaces ramenées à un tiret, séparateurs simples.
 *
 * C'est cette forme qui sert à vérifier l'unicité dans la galerie (4.2).
 */
export function formatInventoryNumber(numero) {
    const propre = numero
        .normalize('NFKC')
        .trim()
        .toUpperCase()
        .replace(/[\s_]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-|-$/g, '');
    if (propre === '') {
        throw new AppError('validation.champ_manquant', 'Le numéro d’inventaire est vide. Saisissez-en un, ou laissez Artosera le proposer.');
    }
    return propre;
}
const UNITES_OCTETS = {
    fr: ['o', 'Ko', 'Mo', 'Go'],
    en: ['B', 'KB', 'MB', 'GB'],
};
/** Une taille de fichier : « 402,6 Ko », « 402.6 KB ». */
export function formatOctets(octets, lang) {
    let valeur = octets;
    let rang = 0;
    while (valeur >= 1024 && rang < UNITES_OCTETS[lang].length - 1) {
        valeur /= 1024;
        rang += 1;
    }
    const nombre = new Intl.NumberFormat(locale(lang), {
        maximumFractionDigits: rang === 0 ? 0 : 1,
    }).format(valeur);
    return `${nombre}\u00a0${UNITES_OCTETS[lang][rang]}`;
}
/** Le texte d'un champ bilingue dans la langue demandée, le français en repli (D15). */
export function texteEnLangue(texte, lang) {
    if (lang === 'en' && texte.en !== undefined && texte.en.trim() !== '')
        return texte.en;
    return texte.fr;
}
//# sourceMappingURL=index.js.map